import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useModuleLicense } from '../../hooks/useModuleLicense';

/**
 * Shared HR-license gate for the WHS management console. Mirrors the standard
 * pattern used across HR pages: loading spinner → AccessRestricted → children.
 * Children only mount once access is granted, so their data-loading effects
 * never fire for unlicensed users.
 *
 * NOTE: worker-facing pages (report a hazard, track a report) must NOT use this
 * — they are available to any authenticated user (ISO worker participation).
 */
export default function WhsGate({ children }: { children: ReactNode }) {
  const { hasAccess, loading } = useModuleLicense('timesheet', 'hr');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR license seat is required to access the WHS console.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
